/**
 * HomeTour — product tour 1x di homepage (MVP, 2026-08-15).
 *
 * Konsep utama aplikasi adalah swipe-the-card: user memilih worksheet
 * dengan menggeser kartu. Tour ini muncul SEKALI per device saat home
 * pertama kali mencapai state ready (kartu ter-load), lalu menulis flag
 * ke localStorage supaya tidak muncul lagi.
 *
 * Selain menjelaskan cara swipe, tour ini juga memberi tahu user bahwa
 * Funpaper bisa "dipasang" ke layar utama HP (tanpa menyebut istilah
 * teknis). Bagian pasang hanya tampil jika belum terpasang:
 *   - Ada event `beforeinstallprompt` → tampilkan tombol "Pasang sekarang"
 *     yang memanggil prompt browser.
 *   - Tidak ada event (mis. iOS Safari) → tampilkan panduan singkat
 *     "buka menu browser, pilih Tambahkan ke Layar Utama".
 *   - Sudah terpasang (display-mode: standalone) → bagian ini disembunyikan.
 *
 * Catatan:
 *  - Mengikuti pola storage device-id.ts / parent-session.ts: localStorage
 *    dengan guard `typeof localStorage !== 'undefined'`. Di native (tanpa
 *    localStorage) flag tidak persist — tour bisa muncul lagi per sesi.
 *    TODO(native): pindahkan ke AsyncStorage saat target native aktif.
 *  - Pola modal meniru SegeraHadirModal (RN Modal + backdrop Pressable).
 *  - Delay tampil 600ms supaya kartu deck sempat render dulu — user
 *    melihat konteks (stack kartu) sebelum overlay muncul.
 */

import { useEffect, useRef, useState } from 'react';
import { Modal, Pressable, StyleSheet, Text, View } from 'react-native';

import { Brand, Radii, Spacing } from '@/constants/theme';

const STORAGE_KEY = 'funpaper.homeTourSeen.v1';
const SHOW_DELAY_MS = 600;

/** Event `beforeinstallprompt` — sinyal browser bahwa aplikasi siap dipasang. */
type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

function hasSeenTour(): boolean {
  if (typeof localStorage === 'undefined') return false;
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function markTourSeen(): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, '1');
  } catch {
    // localStorage disabled (private mode / quota) — tour muncul lagi nanti.
  }
}

function isRunningInstalled(): boolean {
  if (typeof window === 'undefined') return false;
  const standalone =
    window.matchMedia?.('(display-mode: standalone)').matches ?? false;
  const iosStandalone =
    typeof navigator !== 'undefined' &&
    (navigator as unknown as { standalone?: boolean }).standalone === true;
  return standalone || iosStandalone;
}

export function HomeTour() {
  const [visible, setVisible] = useState(false);
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(
    null,
  );
  const [installed, setInstalled] = useState(false);
  const installedRef = useRef(false);

  useEffect(() => {
    if (hasSeenTour()) return;
    const timer = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  // Deteksi status pemasangan + tahan event `beforeinstallprompt` saat muncul.
  useEffect(() => {
    const alreadyInstalled = isRunningInstalled();
    installedRef.current = alreadyInstalled;
    setInstalled(alreadyInstalled);
    if (typeof window === 'undefined') return;
    const onBeforeInstall = (e: Event) => {
      // Once the one-time tour is dismissed, leave the browser's native
      // install prompt untouched for future visits.
      if (installedRef.current || hasSeenTour()) return;
      e.preventDefault();
      setInstallEvent(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      installedRef.current = true;
      setInstalled(true);
      setInstallEvent(null);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const dismiss = () => {
    markTourSeen();
    setVisible(false);
  };

  const onInstall = async () => {
    if (!installEvent) return;
    await installEvent.prompt();
    await installEvent.userChoice;
    setInstallEvent(null);
  };

  if (!visible) return null;

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={dismiss}
    >
      <Pressable style={styles.backdrop} onPress={dismiss}>
        {/* Inner Pressable mencegah tap backdrop menutup saat tap di kartu. */}
        <Pressable style={styles.card} onPress={(e) => e.stopPropagation?.()}>
          <Text style={styles.emoji}>👆</Text>
          <Text style={styles.title}>Geser kartunya, yuk!</Text>
          <Text style={styles.body}>
            Setiap hari kami siapkan tumpukan worksheet pilihan. Cara
            memilihnya gampang:
          </Text>

          <View style={styles.howTo}>
            <View style={styles.howToRow}>
              <View style={[styles.arrowBadge, styles.arrowRight]}>
                <Text style={styles.arrowText}>→</Text>
              </View>
              <Text style={styles.howToText}>
                <Text style={styles.howToBold}>Geser ke kanan</Text>
                {' '}untuk memilih worksheet
              </Text>
            </View>
            <View style={styles.howToRow}>
              <View style={[styles.arrowBadge, styles.arrowLeft]}>
                <Text style={styles.arrowText}>←</Text>
              </View>
              <Text style={styles.howToText}>
                <Text style={styles.howToBold}>Geser ke kiri</Text>
                {' '}kalau mau lewati dulu
              </Text>
            </View>
          </View>

          <Text style={styles.body}>
            Worksheet yang kamu pilih langsung siap dicetak untuk si kecil.
          </Text>

          {/* Bagian "simpan ke layar utama" — hanya jika belum terpasang. */}
          {!installed ? (
            <View style={styles.installBox}>
              <Text style={styles.installTitle}>📲 Simpan Funpaper di layar utama</Text>
              <Text style={styles.installBody}>
                Buka Funpaper seperti aplikasi biasa — ikonnya muncul di HP-mu,
                tanpa harus mengetik alamat web lagi.
              </Text>
              {installEvent ? (
                <Pressable
                  onPress={onInstall}
                  style={({ pressed }) => [
                    styles.installBtn,
                    pressed && styles.ctaPressed,
                  ]}
                  accessibilityRole="button"
                  accessibilityLabel="Pasang Funpaper ke layar utama"
                >
                  <Text style={styles.installBtnText}>Pasang sekarang</Text>
                </Pressable>
              ) : (
                <Text style={styles.installHint}>
                  Buka menu browser kamu, lalu pilih “Tambahkan ke Layar Utama”.
                </Text>
              )}
            </View>
          ) : null}

          <Pressable
            onPress={dismiss}
            style={({ pressed }) => [styles.cta, pressed && styles.ctaPressed]}
            accessibilityRole="button"
            accessibilityLabel="Mengerti, mulai memilih worksheet"
          >
            <Text style={styles.ctaText}>Mengerti, mulai!</Text>
          </Pressable>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: Spacing.four,
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: Radii.medium,
    padding: Spacing.four,
    width: '100%',
    maxWidth: 360,
    alignItems: 'center',
    gap: Spacing.two,
  },
  emoji: {
    fontSize: 40,
  },
  title: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1A0B5C',
    textAlign: 'center',
  },
  body: {
    fontSize: 14,
    lineHeight: 20,
    color: '#4b5563',
    textAlign: 'center',
  },
  howTo: {
    alignSelf: 'stretch',
    gap: Spacing.two,
    paddingVertical: Spacing.one,
  },
  howToRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.two,
  },
  arrowBadge: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
  },
  arrowRight: {
    backgroundColor: 'rgba(46, 160, 90, 0.12)',
  },
  arrowLeft: {
    backgroundColor: 'rgba(229, 62, 62, 0.10)',
  },
  arrowText: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1A0B5C',
  },
  howToText: {
    flex: 1,
    fontSize: 14,
    lineHeight: 20,
    color: '#4b5563',
  },
  howToBold: {
    fontWeight: '700',
    color: '#1A0B5C',
  },
  installBox: {
    alignSelf: 'stretch',
    marginTop: Spacing.one,
    backgroundColor: 'rgba(86, 63, 122, 0.06)',
    borderWidth: 1,
    borderColor: 'rgba(86, 63, 122, 0.15)',
    borderRadius: Radii.medium,
    padding: Spacing.three,
    gap: Spacing.one,
  },
  installTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: '#1A0B5C',
    textAlign: 'center',
  },
  installBody: {
    fontSize: 13,
    lineHeight: 18,
    color: '#4b5563',
    textAlign: 'center',
  },
  installBtn: {
    marginTop: Spacing.one,
    alignSelf: 'stretch',
    backgroundColor: Brand.primary,
    paddingVertical: 10,
    borderRadius: Radii.small,
    alignItems: 'center',
  },
  installBtnText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '700',
  },
  installHint: {
    marginTop: Spacing.one,
    fontSize: 13,
    lineHeight: 18,
    color: '#1A0B5C',
    fontStyle: 'italic',
    textAlign: 'center',
  },
  cta: {
    alignSelf: 'stretch',
    marginTop: Spacing.two,
    backgroundColor: Brand.primary,
    paddingVertical: 12,
    borderRadius: Radii.small,
    alignItems: 'center',
  },
  ctaText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '700',
  },
  ctaPressed: {
    opacity: 0.75,
  },
});
